import React, { useEffect, useState } from "react";
import { Outlet, Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Dices, Trophy, FileText, Mail, MousePointerClick,
  Tag, MessageSquare, Bug, LogOut, Inbox, Menu, X,
} from "lucide-react";
import { api } from "@/lib/api";
import { safeStorage } from "@/lib/safeStorage";

const ITEMS = [
  { to: "/admin/dashboard", label: "Áttekintés", icon: LayoutDashboard },
  { to: "/admin/kaszinok", label: "Kaszinók", icon: Dices },
  { to: "/admin/bukmekerek", label: "Bukmékerek", icon: Trophy },
  { to: "/admin/promo-kodok", label: "Promóciós kódok", icon: Tag },
  { to: "/admin/blog", label: "Blog", icon: FileText },
  { to: "/admin/feliratkozok", label: "Feliratkozók", icon: Mail },
  { to: "/admin/kattintasok", label: "Kattintások", icon: MousePointerClick },
  { to: "/admin/velemenyek", label: "Vélemények", icon: MessageSquare },
  { to: "/admin/hibajelentesek", label: "Hibajelentések", icon: Bug },
  { to: "/admin/uzenetek", label: "Üzenetek", icon: Inbox },
];

export default function AdminLayout() {
  const [me, setMe] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const token = safeStorage.getItem("bv_admin_token");
    if (!token) {
      navigate("/admin");
      return;
    }
    api.get("/auth/me")
      .then((r) => setMe(r.data))
      .catch(() => {
        safeStorage.removeItem("bv_admin_token");
        navigate("/admin");
      });
  }, [navigate]);

  // Close mobile sidebar on route change
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Lock body scroll when mobile sidebar is open
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [sidebarOpen]);

  const logout = () => {
    safeStorage.removeItem("bv_admin_token");
    navigate("/admin");
  };

  if (!me) return <div className="p-10 text-center text-white/70">Töltés...</div>;

  const currentLabel = ITEMS.find(i => location.pathname.startsWith(i.to))?.label || "Admin";

  const SidebarContent = (
    <>
      <Link to="/" className="font-display font-black text-xl mb-6 flex items-center gap-2" data-testid="admin-logo-link">
        <div className="w-8 h-8 rounded bg-gold flex items-center justify-center text-background font-black">B</div>
        <span>Admin</span>
      </Link>
      <nav className="space-y-1 flex-1 overflow-y-auto -mx-1 px-1">
        {ITEMS.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            className={({ isActive }) =>
              `flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm transition-colors ${isActive ? "bg-gold/10 text-gold" : "text-white/70 hover:bg-white/5 hover:text-white"}`
            }
            data-testid={`admin-nav-${it.to.split("/").pop()}`}
          >
            <it.icon size={16} />
            {it.label}
          </NavLink>
        ))}
      </nav>
      <button onClick={logout} className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-white/70 hover:bg-rose-500/10 hover:text-rose-300 mt-2" data-testid="admin-logout">
        <LogOut size={16} /> Kijelentkezés
      </button>
    </>
  );

  return (
    <div className="min-h-screen bg-background text-white" data-testid="admin-layout">
      {/* Mobile top bar */}
      <header className="md:hidden sticky top-0 z-30 flex items-center justify-between px-4 h-14 border-b border-white/10 bg-card/80 backdrop-blur" data-testid="admin-mobile-topbar">
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 -ml-2 rounded-lg hover:bg-white/5"
          aria-label="Menü megnyitása"
          data-testid="admin-mobile-menu-open"
        >
          <Menu size={22} />
        </button>
        <div className="font-display font-bold text-base flex items-center gap-2">
          <div className="w-6 h-6 rounded bg-gold flex items-center justify-center text-background font-black text-xs">B</div>
          <span>{currentLabel}</span>
        </div>
        <div className="w-8" /> {/* spacer */}
      </header>

      {/* Mobile drawer overlay */}
      {sidebarOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
          data-testid="admin-mobile-backdrop"
          aria-hidden="true"
        />
      )}

      <div className="flex">
        {/* Sidebar — mobile drawer + desktop static */}
        <aside
          className={
            "fixed md:sticky top-0 md:top-0 h-screen z-50 w-72 md:w-64 shrink-0 border-r border-white/10 bg-card p-4 flex flex-col transition-transform duration-300 ease-out " +
            (sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0")
          }
          data-testid="admin-sidebar"
        >
          {/* Mobile close button */}
          <button
            onClick={() => setSidebarOpen(false)}
            className="md:hidden absolute top-3 right-3 p-2 rounded-lg hover:bg-white/5"
            aria-label="Menü bezárása"
            data-testid="admin-mobile-menu-close"
          >
            <X size={20} />
          </button>
          {SidebarContent}
        </aside>

        {/* Main content */}
        <main className="flex-1 min-w-0 p-4 md:p-6 lg:p-10 overflow-x-auto" data-testid="admin-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
